import { TypeChecker } from '@attain-apps/type-guards'

import { Tag } from './tag'

// TODO: maybe move these to their own library?

// TODO: add jsdocs

export interface Identified<TId extends Identified.Id = Identified.Id> {
  id: TId
}
export namespace Identified {
  export function getTypeChecker<TId extends Id>(
    isId: TypeChecker<TId>,
  ): TypeChecker<Identified<TId>> {
    return TypeChecker.Factory.object<Identified<TId>>({ id: isId })
  }
  export const isIdentified = TypeChecker.Factory.lazy(() =>
    getTypeChecker(Id.isId),
  )

  export type Id = string | number | symbol
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.Factory.union(
      TypeChecker.isString,
      TypeChecker.isNumber,
      TypeChecker.isSymbol,
    ).usingAlias('Identified.Id')

    export type From<TIdentified extends Identified = Identified> =
      TIdentified extends Identified<infer TId> ? TId : never
  }

  export type Optionally<TId extends Id = Id> = Partial<Identified<TId>>
  export namespace Optionally {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getTypeChecker<TId extends Id>(
      isId: TypeChecker<TId>,
    ): TypeChecker<Optionally<TId>> {
      return TypeChecker.Factory.object<Optionally<TId>>({ id: isId })
    }
    export const isOptionally = TypeChecker.Factory.lazy(() =>
      getTypeChecker(Id.isId),
    )
  }
}

export interface Named {
  name: string
}
export namespace Named {
  export const isNamed = TypeChecker.Factory.object<Named>({
    name: TypeChecker.isString,
  })

  export type Optionally = Partial<Named>
  export namespace Optionally {
    export const isOptionally = TypeChecker.Factory.object<Optionally>({
      name: TypeChecker.Factory.optional(TypeChecker.isString),
    })
  }
}
/** @deprecated Use {@link Named.Optionally} */
export type PotentiallyNamed = Partial<Named>
export namespace PotentiallyNamed {
  /** @deprecated Use {@link Named.Optionally.isOptionally} */
  export const isPotentiallyNamed =
    TypeChecker.Factory.object<PotentiallyNamed>({
      name: TypeChecker.Factory.optional(TypeChecker.isString),
    })
}

export interface Coded {
  code: string
}
export namespace Coded {
  export const isCoded = TypeChecker.Factory.object<Coded>({
    code: TypeChecker.isString,
  })

  export type Optionally = Partial<Coded>
  export namespace Optionally {
    export const isOptionally = TypeChecker.Factory.object<Optionally>({
      code: TypeChecker.Factory.optional(TypeChecker.isString),
    })
  }
}
/** @deprecated Use {@link Coded.Optionally} */
export type PotentiallyCoded = Partial<Coded>
export namespace PotentiallyCoded {
  /** @deprecated Use {@link Coded.Optionally.isOptionally} */
  export const isPotentiallyCoded =
    TypeChecker.Factory.object<PotentiallyCoded>({
      code: TypeChecker.Factory.optional(TypeChecker.isString),
    })
}

export interface Described {
  description: string
}
export namespace Described {
  export const isDescribed = TypeChecker.Factory.object<Described>({
    description: TypeChecker.isString,
  })

  export type Optionally = Partial<Described>
  export namespace Optionally {
    export const isOptionally = TypeChecker.Factory.object<Optionally>({
      description: TypeChecker.Factory.optional(TypeChecker.isString),
    })
  }
}
/** @deprecated Use {@link Described.Optionally} */
export type PotentiallyDescribed = Partial<Described>
export namespace PotentiallyDescribed {
  /** @deprecated Use {@link Described.Optionally.isOptionally} */
  export const isPotentiallyDescribed =
    TypeChecker.Factory.object<PotentiallyDescribed>({
      description: TypeChecker.Factory.optional(TypeChecker.isString),
    })
}

/**
 * There are cases where one might want to identify an item, but cannot use the
 * ID. Some common reasons:
 * 1. The ID is too long
 * 1. The ID is not very readable
 * 1. The item needs to be identified before it's created
 * 1. It needs to be easy to switch out the item with a replacement, which will
 *    have a new, generated ID
 *
 * When one or more of these issues arise, it can be useful to reference this
 * {@link Aliased.alias} as an alternative identifier
 */
export interface Aliased {
  /**
   * A readable, mutable identifier for this item
   */
  alias: string
}
export namespace Aliased {
  export const isAliased = TypeChecker.Factory.object<Aliased>({
    alias: TypeChecker.isString,
  })

  export type Optionally = Partial<Aliased>
  export namespace Optionally {
    export const isOptionally = TypeChecker.Factory.object<Optionally>({
      alias: TypeChecker.Factory.optional(TypeChecker.isString),
    })
  }
}

export interface Tagged {
  tags?: readonly Tag[]
}
export namespace Tagged {
  export const isTagged = TypeChecker.Factory.object<Tagged>({
    tags: TypeChecker.Factory.optional(TypeChecker.Factory.list(Tag.isTag)),
  })
}

/**
 * Identifies the user who created an item.
 */
export interface CreatedBy<TUserId extends string = string> {
  /**
   * ID of the user who created this item.
   *
   * This is optional because existing or system-created records may not have a
   * signed-in creator.
   */
  createdBy?: TUserId
}

export interface PotentiallyOptional {
  isOptional: boolean
}
export namespace PotentiallyOptional {
  export const isPotentiallyOptional =
    TypeChecker.Factory.object<PotentiallyOptional>({
      isOptional: TypeChecker.isBoolean,
    })
}

export interface PotentiallyNew {
  isNew: boolean
}
export namespace PotentiallyNew {
  export const isPotentiallyNew = TypeChecker.Factory.object<PotentiallyNew>({
    isNew: TypeChecker.isBoolean,
  })
}

// NOTE: not optional because there is not an obvious default
export interface Activateable {
  isActive: boolean
}
export namespace Activateable {
  export const isActivateable = TypeChecker.Factory.object<Activateable>({
    isActive: TypeChecker.isBoolean,
  })
}

export interface Hideable {
  /**
   * @default false
   */
  isHidden?: boolean
}
export namespace Hideable {
  export const isHideable = TypeChecker.Factory.object<Hideable>({
    isHidden: TypeChecker.Factory.optional(TypeChecker.isBoolean),
  })
}

export interface Positioned {
  position: number
}
export namespace Positioned {
  export const isPositioned = TypeChecker.Factory.object<Positioned>({
    position: TypeChecker.isNumber,
  })
}

export interface Prioritized {
  priority: number
}
export namespace Prioritized {
  export const isPrioritized = TypeChecker.Factory.object<Prioritized>({
    priority: TypeChecker.isNumber,
  })
}

export interface Colored {
  /**
   * TODO: figure out if this is meant to be a hex-code, enum, etc.
   */
  color?: string
}
export namespace Colored {
  export const isColored = TypeChecker.Factory.object<Colored>({
    color: TypeChecker.Factory.optional(TypeChecker.isString),
  })
}
